import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { verifyEmail, resendVerificationEmail } from '../../utils/auth/verification';
import { supabase } from '../../utils/supabase';
import { Footer } from '../../components/common/Footer';

export function EmailVerificationPage() {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get email from location state or URL params
  const email = location.state?.email || new URLSearchParams(location.search).get('email');

  useEffect(() => {
    const checkVerification = async () => {
      if (!email) {
        setIsVerifying(false);
        setError('メールアドレスが見つかりません');
        return;
      }

      try {
        // Check if the user's email is already verified
        const { data } = await supabase.auth.getSession();
        if (data.session?.user?.email_confirmed_at) {
          setIsVerified(true);
        } else {
          // If not verified, check if there's a hash in the URL (from email link)
          const hash = window.location.hash;
          if (hash && hash.includes('type=signup')) {
            // This is likely a verification link click
            const { error } = await supabase.auth.refreshSession();
            if (error) {
              console.error('Error refreshing session:', error);
              setError('メールアドレスの確認に失敗しました');
            } else {
              // Check again after refresh
              const { data: refreshedData } = await supabase.auth.getSession();
              setIsVerified(!!refreshedData.session?.user?.email_confirmed_at);
            }
          }
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError('メールアドレスの確認中にエラーが発生しました');
      } finally {
        setIsVerifying(false);
      }
    };

    checkVerification();
  }, [email]);

  const handleResendVerification = async () => {
    if (!email) return;
    
    setIsResending(true);
    setResendSuccess(false);
    setError(null);
    
    try {
      await resendVerificationEmail(email);
      setResendSuccess(true);
    } catch (err) {
      setError('確認メールの再送信に失敗しました');
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    return (
      <div>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8 text-center">
            <div className="mx-auto w-24 h-24 flex items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              エラー
            </h2>
            <p className="mt-2 text-gray-600">
              メールアドレスが見つかりません。新規登録からやり直してください。
            </p>
            <button
              onClick={() => navigate('/signup')}
              className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              新規登録ページへ
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="mx-auto w-24 h-24 flex items-center justify-center rounded-full bg-blue-100">
            <Mail className="w-12 h-12 text-blue-600" />
          </div>

          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            メール認証
          </h2>

          {isVerifying ? (
            <div className="mt-4 flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
              <p className="text-gray-600">認証状態を確認中...</p>
            </div>
          ) : isVerified ? (
            <div className="mt-4">
              <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                <CheckCircle className="w-6 h-6" />
                <span className="text-lg font-medium">メールアドレスの認証が完了しました</span>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                ログインページへ
              </button>
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-gray-600 mb-4">
                {email} 宛に認証メールを送信しました。<br />
                メールに記載されたリンクをクリックして、メールアドレスの認証を完了してください。
              </p>
              
              {error && (
                <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              )}
              
              {resendSuccess && (
                <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                  <CheckCircle className="w-5 h-5" />
                  <span>認証メールを再送信しました</span>
                </div>
              )}
              
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="mt-4 w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>送信中...</span>
                  </>
                ) : (
                  <span>認証メールを再送信</span>
                )}
              </button>
              
              <button
                onClick={() => navigate('/login')}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                ログインページへ戻る
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}